import { useState, useMemo } from 'react'
import type { Organisation } from '@esf/shared'

const renderIcon = (path: string, className = 'w-5 h-5') => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
)

interface OrgStats {
  organisationId: string
  assetCount: number
  userCount: number
  workOrderCount: number
  iotDeviceCount: number
}

interface OrganisationsPageProps {
  organisations: Organisation[]
  orgStats: Record<string, OrgStats>
  onCreateOrg: () => void
  onEditOrg: (org: Organisation) => void
  onToggleOrgEnabled: (org: Organisation) => void
  onViewOrgDetails: (org: Organisation) => void
  isLoading: boolean
}

export default function OrganisationsPage({
  organisations,
  orgStats,
  onCreateOrg,
  onEditOrg,
  onToggleOrgEnabled,
  onViewOrgDetails,
  isLoading,
}: OrganisationsPageProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [showDisabled, setShowDisabled] = useState(false)

  const filteredOrgs = organisations.filter(org => {
    const matchesSearch = searchTerm === '' ||
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.slug.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesEnabled = showDisabled || org.enabled !== false
    return matchesSearch && matchesEnabled
  })

  // Calculate totals across all organisations
  const totals = useMemo(() => {
    const activeOrgs = organisations.filter(o => o.enabled !== false && !o.isEsfOrg)
    let totalAssets = 0
    let totalUsers = 0
    let totalWorkOrders = 0

    for (const org of activeOrgs) {
      const stats = orgStats[org.id]
      if (stats) {
        totalAssets += stats.assetCount
        totalUsers += stats.userCount
        totalWorkOrders += stats.workOrderCount
      }
    }

    return { totalAssets, totalUsers, totalWorkOrders, activeOrgCount: activeOrgs.length }
  }, [organisations, orgStats])

  return (
    <div className="container-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organisations</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage customer organisations and their access
          </p>
        </div>
        <button className="btn btn-primary" onClick={onCreateOrg}>
          {renderIcon('M12 4v16m8-8H4')}
          Add Organisation
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search organisations..."
              className="input w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show disabled
          </label>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="kpi">
          <div className="kpi-icon bg-esf-navy/10 text-esf-navy">
            {renderIcon('M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4')}
          </div>
          <div className="kpi-title">Active Organisations</div>
          <div className="kpi-value">{totals.activeOrgCount}</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon bg-blue-100 text-blue-600">
            {renderIcon('M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4')}
          </div>
          <div className="kpi-title">Total Products</div>
          <div className="kpi-value">{totals.totalAssets.toLocaleString()}</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon bg-purple-100 text-purple-600">
            {renderIcon('M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z')}
          </div>
          <div className="kpi-title">Total Users</div>
          <div className="kpi-value">{totals.totalUsers.toLocaleString()}</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon bg-amber-100 text-amber-600">
            {renderIcon('M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01')}
          </div>
          <div className="kpi-title">Work Orders</div>
          <div className="kpi-value">{totals.totalWorkOrders.toLocaleString()}</div>
        </div>
      </div>

      {/* Organisations Table */}
      <div className="table-card">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading organisations...</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Organisation</th>
                <th>Slug</th>
                <th>Products</th>
                <th>Users</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrgs.map(org => {
                const stats = orgStats[org.id]
                return (
                  <tr key={org.id} className={org.enabled === false ? 'opacity-50' : ''}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-esf-navy/10 flex items-center justify-center text-esf-navy font-bold">
                          {org.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium">{org.name}</div>
                          {org.isEsfOrg && (
                            <span className="text-xs text-esf-coral font-medium">ESF Platform</span>
                          )}
                          {org.contactEmail && !org.isEsfOrg && (
                            <div className="text-xs text-gray-500">{org.contactEmail}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="text-sm text-gray-500 font-mono">{org.slug}</td>
                    <td className="text-sm">{stats?.assetCount ?? '-'}</td>
                    <td className="text-sm">{stats?.userCount ?? '-'}</td>
                    <td>
                      <span className={`badge ${org.enabled !== false ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'}`}>
                        {org.enabled !== false ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => onViewOrgDetails(org)}
                          title="View details"
                        >
                          View
                        </button>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => onEditOrg(org)}
                          title="Edit organisation"
                        >
                          Edit
                        </button>
                        {!org.isEsfOrg && (
                          <button
                            className={`btn btn-sm ${org.enabled !== false ? 'btn-secondary' : 'bg-success-50 text-success-600 hover:bg-success-100'}`}
                            onClick={() => onToggleOrgEnabled(org)}
                            title={org.enabled !== false ? 'Disable organisation' : 'Enable organisation'}
                          >
                            {org.enabled !== false ? 'Disable' : 'Enable'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
              {filteredOrgs.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <div className="empty-state-icon bg-esf-navy/10 text-esf-navy">
                        {renderIcon('M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4')}
                      </div>
                      <div className="empty-state-title">No Organisations</div>
                      <div className="empty-state-text">
                        {searchTerm ? 'No organisations match your search.' : 'Add your first customer organisation to get started.'}
                      </div>
                      {!searchTerm && (
                        <button className="btn btn-primary mt-4" onClick={onCreateOrg}>
                          {renderIcon('M12 4v16m8-8H4')}
                          Add Organisation
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
